import { BACKEND_URL } from "../../lib/backend";
/**
 * ConsentSourcesWidget — Subscriber acquisition analytics by consent_source.
 *
 * Backed by GET /api/admin/analytics/consent-sources?days=N.
 * Shows: which surfaces convert (footer, popup, landing, mobile_ios, ...),
 * conversion rate (confirmed / signups), share of total signups, and
 * a 14-day daily signup sparkline.
 *
 * Self-contained: handles its own data fetching.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, RefreshCw, TrendingUp } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

const SOURCE_LABELS = {
  footer: "Footer (subscribe link)",
  website: "Website (generic)",
  auth_page: "Auth page CTA",
  verify_resend: "Verify email — resend",
  reactivation: "Reactivation (re-subscribe)",
  mobile_ios: "iOS app",
  mobile_android: "Android app",
  popup: "Popup overlay",
  unknown: "Unknown / legacy",
};

const labelFor = (src) => SOURCE_LABELS[src] || src;

export default function ConsentSourcesWidget({ dm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async (windowDays) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/analytics/consent-sources?days=${windowDays}`);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load consent-source analytics"));
    } finally {
      setLoading(false);
    }
  }, []);

   
  useEffect(() => { load(days); }, [days, load]);

  const maxCount = data?.sources?.length ? Math.max(...data.sources.map(s => s.count)) : 1;
  const maxDaily = data?.by_day?.length ? Math.max(...data.by_day.map(d => d.new_signups), 1) : 1;

  return (
    <div
      className={`rounded-xl border p-6 ${dm ? "bg-[#1a2122] border-[#2a3435]" : "bg-white border-[#e8ecee]"}`}
      data-testid="consent-sources-widget"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-500/10 text-emerald-600"}`}>
          <Users className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`editorial-heading text-base font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            Subscribers by acquisition source
          </h3>
          <p className={`category-tag text-[11px] mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Where new subscribers come from — drives where to invest UX effort.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          data-testid="consent-sources-days-select"
          className={`text-xs rounded-md px-2 py-1.5 ${dm ? "bg-[#252e2f] text-slate-300 border border-[#2a3435]" : "bg-[#f3f4f5] text-[#191c1d] border border-[#e8ecee]"}`}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
        <button
          onClick={() => load(days)}
          disabled={loading}
          data-testid="consent-sources-refresh-btn"
          className={`p-1.5 rounded-md transition-colors ${dm ? "hover:bg-white/5 text-slate-400" : "hover:bg-[#f3f4f5] text-[#6b7a7d]"}`}
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && !data ? (
        <div className={`text-center py-6 text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Loading…</div>
      ) : !data ? (
        <div className={`text-center py-6 text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>No data yet.</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className={`rounded-md p-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
              <p className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>New signups</p>
              <p className={`text-xl font-semibold mt-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`} data-testid="consent-total-users">{data.total_users}</p>
            </div>
            <div className={`rounded-md p-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
              <p className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Confirmed</p>
              <p className={`text-xl font-semibold mt-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`} data-testid="consent-total-confirmed">{data.total_confirmed}</p>
            </div>
            <div className={`rounded-md p-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
              <p className={`category-tag text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Conversion</p>
              <p className={`text-xl font-semibold mt-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`} data-testid="consent-overall-conversion">
                {data.total_users > 0 ? `${Math.round((data.total_confirmed / data.total_users) * 100)}%` : "—"}
              </p>
            </div>
          </div>

          {/* Source breakdown bars */}
          <div className="space-y-2 mb-5" data-testid="consent-source-bars">
            {data.sources?.length === 0 && (
              <p className={`text-sm text-center py-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                No signups in this window.
              </p>
            )}
            {data.sources?.map((s) => (
              <div key={s.source} className="flex items-center gap-3" data-testid={`consent-source-row-${s.source}`}>
                <span className={`category-tag text-xs w-44 truncate flex-shrink-0 ${dm ? "text-slate-300" : "text-[#191c1d]"}`} title={s.source}>
                  {labelFor(s.source)}
                </span>
                <div className={`flex-1 rounded-full h-2 overflow-hidden ${dm ? "bg-[#2a3435]" : "bg-[#e8ecee]"}`}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(s.count / maxCount) * 100}%`,
                      background: s.source === "footer" ? "#10b981" : "#006874",
                    }}
                  />
                </div>
                <span className={`category-tag text-xs w-8 text-right flex-shrink-0 ${dm ? "text-slate-300" : "text-[#191c1d]"}`}>{s.count}</span>
                <span className={`category-tag text-xs w-12 text-right flex-shrink-0 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  {Math.round(s.share * 100)}%
                </span>
                <span
                  className={`category-tag text-xs w-14 text-right flex-shrink-0 ${
                    s.conversion_rate >= 0.5
                      ? (dm ? "text-emerald-400" : "text-emerald-600")
                      : s.conversion_rate >= 0.2
                      ? (dm ? "text-amber-400" : "text-amber-600")
                      : (dm ? "text-slate-500" : "text-[#6b7a7d]")
                  }`}
                  title="Confirmation rate (confirmed / signups)"
                >
                  {Math.round(s.conversion_rate * 100)}%
                </span>
              </div>
            ))}
          </div>

          {/* 14-day sparkline */}
          {data.by_day?.length > 0 && (
            <div className={`rounded-md p-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`} data-testid="consent-sparkline">
              <p className={`category-tag text-xs mb-2 flex items-center gap-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                <TrendingUp className="w-3 h-3" />
                Daily signups — last 14 days
              </p>
              <div className="flex items-end gap-1 h-12">
                {data.by_day.map((d) => {
                  const pct = (d.new_signups / maxDaily) * 100;
                  return (
                    <div
                      key={d.day}
                      className="flex-1 rounded-t bg-[#006874] dark:bg-cyan-400"
                      style={{ height: `${Math.max(pct, 4)}%`, minHeight: 2 }}
                      title={`${d.day}: ${d.new_signups} signup${d.new_signups === 1 ? "" : "s"}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
